interface TimeSlot {
  start: string;
  end: string;
}

interface DayAvailability {
  enabled: boolean;
  timeSlots: TimeSlot[];
}

interface WeeklyAvailability {
  monday: DayAvailability;
  tuesday: DayAvailability;
  wednesday: DayAvailability;
  thursday: DayAvailability;
  friday: DayAvailability;
  saturday: DayAvailability;
  sunday: DayAvailability;
}

/**
 * Gets the user's current timezone (fallback to browser timezone)
 */
export const getUserTimezone = (userTimezone?: string): string => {
  if (userTimezone && userTimezone !== 'Select timezone') {
    return userTimezone;
  }

  // Fallback to browser timezone
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
};

import { fromZonedTime, formatInTimeZone } from 'date-fns-tz';

/**
 * Converts a time string (HH:MM) from one timezone to another
 * Using the reliable date-fns-tz library
 */
export const convertTimeToTimezone = (
  time: string,
  fromTimezone: string,
  toTimezone: string,
  date?: Date
): string => {
  try {
    // Parse the time
    const [hours, minutes] = time.split(':').map(Number);

    // Use provided date or today as reference for DST calculations
    const referenceDate = date || new Date();
    const year = referenceDate.getFullYear();
    const month = String(referenceDate.getMonth() + 1).padStart(2, '0');
    const day = String(referenceDate.getDate()).padStart(2, '0');

    // For Local to UTC conversion
    if (toTimezone === 'UTC') {
      // SIMPLE MANUAL APPROACH - until we get the library working correctly

      // Create a temporary date to figure out the current timezone offset for the source timezone
      const tempDate = new Date();

      // Get the time as it would appear in the source timezone
      const sourceTimeString = tempDate.toLocaleString('sv-SE', { timeZone: fromTimezone });
      const utcTimeString = tempDate.toLocaleString('sv-SE', { timeZone: 'UTC' });

      const sourceTime = new Date(sourceTimeString);
      const utcTime = new Date(utcTimeString);

      // Calculate offset: UTC time - Source time = offset to add to source time to get UTC
      const offsetMs = utcTime.getTime() - sourceTime.getTime();
      const offsetHours = offsetMs / (1000 * 60 * 60);

      // Apply this offset to our input time
      const inputHours = parseInt(time.split(':')[0]);
      const inputMinutes = parseInt(time.split(':')[1]);

      const utcHours = (inputHours + offsetHours + 24) % 24; // Add 24 and mod to handle negatives
      const result = `${String(Math.floor(utcHours)).padStart(2, '0')}:${String(inputMinutes).padStart(2, '0')}`;

      return result;
    }

    // For UTC to Local conversion
    if (fromTimezone === 'UTC') {
      const utcDate = new Date(Date.UTC(year, Number(month) - 1, Number(day), hours, minutes, 0, 0));
      const result = formatInTimeZone(utcDate, toTimezone, 'HH:mm');
      return result;
    }

    // For other conversions, go through UTC
    const utcTime = convertTimeToTimezone(time, fromTimezone, 'UTC', date);
    return convertTimeToTimezone(utcTime, 'UTC', toTimezone, date);

  } catch {
    return time;
  }
};

/**
 * Converts a time string (HH:MM) from user's local timezone to GMT
 */
export const convertTimeToGMT = (
  time: string,
  userTimezone: string,
  date?: Date
): string => {
  return convertTimeToTimezone(time, userTimezone, 'UTC', date);
};

/**
 * Converts a time string (HH:MM) from GMT to user's local timezone
 */
export const convertTimeFromGMT = (
  time: string,
  userTimezone: string,
  date?: Date
): string => {
  return convertTimeToTimezone(time, 'UTC', userTimezone, date);
};

/**
 * Converts availability times from user's local timezone to GMT for storage
 */
export const convertAvailabilityToGMT = (
  availability: WeeklyAvailability,
  userTimezone: string
): WeeklyAvailability => {
  if (!availability || typeof availability !== 'object') {
    return getEmptyAvailability();
  }

  const convertedAvailability: WeeklyAvailability = getEmptyAvailability();
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;

  days.forEach((day, dayIndex) => {
    const dayAvailability = availability[day];

    if (!dayAvailability || !dayAvailability.enabled || !dayAvailability.timeSlots?.length) {
      convertedAvailability[day] = { enabled: false, timeSlots: [] };
      return;
    }

    // Get a reference date for this day of the week
    const today = new Date();
    const targetDate = getDateForDayOfWeek(today, dayIndex);

    const convertedTimeSlots: TimeSlot[] = [];

    dayAvailability.timeSlots.forEach((slot) => {
      if (!slot?.start || !slot?.end) {
        return;
      }

      try {
        const startTimeGMT = convertTimeToGMT(slot.start, userTimezone, targetDate);
        const endTimeGMT = convertTimeToGMT(slot.end, userTimezone, targetDate);

        convertedTimeSlots.push({
          start: startTimeGMT,
          end: endTimeGMT
        });
      } catch {
        // Skip invalid time slots
      }
    });

    convertedAvailability[day] = {
      enabled: convertedTimeSlots.length > 0,
      timeSlots: convertedTimeSlots
    };
  });

  return convertedAvailability;
};

/**
 * Converts availability times from GMT to user's local timezone for display
 */
export const convertAvailabilityFromGMT = (
  availability: WeeklyAvailability,
  userTimezone: string
): WeeklyAvailability => {
  if (!availability || typeof availability !== 'object') {
    return getEmptyAvailability();
  }

  const convertedAvailability: WeeklyAvailability = getEmptyAvailability();
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;

  days.forEach((day, dayIndex) => {
    const dayAvailability = availability[day];

    if (!dayAvailability || !dayAvailability.enabled || !dayAvailability.timeSlots?.length) {
      convertedAvailability[day] = { enabled: false, timeSlots: [] };
      return;
    }

    // Get a reference date for this day of the week
    const today = new Date();
    const targetDate = getDateForDayOfWeek(today, dayIndex);

    const convertedTimeSlots: TimeSlot[] = [];

    dayAvailability.timeSlots.forEach((slot) => {
      if (!slot?.start || !slot?.end) {
        return;
      }

      try {
        const startTimeLocal = convertTimeFromGMT(slot.start, userTimezone, targetDate);
        const endTimeLocal = convertTimeFromGMT(slot.end, userTimezone, targetDate);

        convertedTimeSlots.push({
          start: startTimeLocal,
          end: endTimeLocal
        });
      } catch {
        // Skip invalid time slots
      }
    });

    convertedAvailability[day] = {
      enabled: convertedTimeSlots.length > 0,
      timeSlots: convertedTimeSlots
    };
  });

  return convertedAvailability;
};

/**
 * Converts a meeting datetime from GMT to user's local timezone
 */
export const convertMeetingTimeFromGMT = (
  gmtDateTime: string,
  userTimezone: string
): { date: string; time: string; datetime: string } => {
  try {
    const gmtDate = new Date(gmtDateTime);

    // Use the native JavaScript timezone formatting (this part actually works fine)
    // since it's working with full datetime objects, not just time strings
    const localDate = gmtDate.toLocaleDateString('en-US', {
      timeZone: userTimezone,
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    const localTime = gmtDate.toLocaleTimeString('en-US', {
      timeZone: userTimezone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });

    // Use formatInTimeZone from date-fns-tz for consistent datetime conversion
    const localDateTime = formatInTimeZone(gmtDate, userTimezone, "yyyy-MM-dd'T'HH:mm:ss.SSSxxx");

    return {
      date: localDate,
      time: localTime,
      datetime: localDateTime
    };
  } catch {
    return {
      date: 'Invalid Date',
      time: 'Invalid Time',
      datetime: gmtDateTime
    };
  }
};

/**
 * Helper function to parse human-readable date and time into ISO format
 */
export const parseHumanDateTimeToISO = (
  date: string,
  time: string
): string => {
  // Parse the date (e.g., "Thursday, September 25, 2025")
  const parsedDate = new Date(date);
  if (isNaN(parsedDate.getTime())) {
    throw new Error(`Invalid date format: ${date}`);
  }

  // Parse the time - handle both 12-hour (e.g., "9:00 PM") and 24-hour (e.g., "23:00") formats
  let hours: number;
  let minutes: number;

  const twelveHourMatch = time.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  const twentyFourHourMatch = time.match(/(\d{1,2}):(\d{2})$/);

  if (twelveHourMatch) {
    // 12-hour format with AM/PM
    hours = parseInt(twelveHourMatch[1], 10);
    minutes = parseInt(twelveHourMatch[2], 10);
    const ampm = twelveHourMatch[3].toUpperCase();

    // Convert to 24-hour format
    if (ampm === 'PM' && hours !== 12) {
      hours += 12;
    } else if (ampm === 'AM' && hours === 12) {
      hours = 0;
    }
  } else if (twentyFourHourMatch) {
    // 24-hour format
    hours = parseInt(twentyFourHourMatch[1], 10);
    minutes = parseInt(twentyFourHourMatch[2], 10);

    // Validate 24-hour format
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
      throw new Error(`Invalid time format: ${time}`);
    }
  } else {
    throw new Error(`Invalid time format: ${time}. Expected formats: "9:00 PM" or "21:00"`);
  }

  // Create ISO string
  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
  const day = String(parsedDate.getDate()).padStart(2, '0');
  const hoursStr = String(hours).padStart(2, '0');
  const minutesStr = String(minutes).padStart(2, '0');

  return `${year}-${month}-${day}T${hoursStr}:${minutesStr}:00`;
};

/**
 * Converts a meeting datetime from user's local timezone to GMT
 * @param date - Human readable date like "Thursday, September 25, 2025"
 * @param time - Human readable time like "9:00 PM"
 * @param userTimezone - User's timezone
 */
export const convertMeetingDateTimeToGMT = (
  date: string,
  time: string,
  userTimezone: string
): string => {
  // Convert human-readable date/time to ISO format first
  const isoDateTime = parseHumanDateTimeToISO(date, time);

  // Then convert to GMT
  return convertMeetingTimeToGMT(isoDateTime, userTimezone);
};

/**
 * Converts a meeting datetime from user's local timezone to GMT
 */
export const convertMeetingTimeToGMT = (
  localDateTime: string,
  userTimezone: string
): string => {
  try {
    // Create date object without timezone interpretation
    const [datePart, timePart] = localDateTime.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hours, minutes] = timePart.split(':').map(Number);

    // Create a local date object representing the time in the user's timezone
    const localDate = new Date(year, month - 1, day, hours, minutes, 0, 0);

    // Use fromZonedTime to convert from user's timezone to UTC
    // This interprets the localDate as if it were in the user's timezone
    const gmtDate = fromZonedTime(localDate, userTimezone);

    return gmtDate.toISOString();
  } catch {
    return localDateTime;
  }
};

// Helper functions

/**
 * Returns an empty availability object
 */
const getEmptyAvailability = (): WeeklyAvailability => ({
  monday: { enabled: false, timeSlots: [] },
  tuesday: { enabled: false, timeSlots: [] },
  wednesday: { enabled: false, timeSlots: [] },
  thursday: { enabled: false, timeSlots: [] },
  friday: { enabled: false, timeSlots: [] },
  saturday: { enabled: false, timeSlots: [] },
  sunday: { enabled: false, timeSlots: [] }
});



/**
 * Gets a Date object for a specific day of the week
 * @param referenceDate - Reference date
 * @param dayIndex - Day index (0 = Monday, 1 = Tuesday, etc.)
 */
const getDateForDayOfWeek = (referenceDate: Date, dayIndex: number): Date => {
  const today = new Date(referenceDate);
  const currentDay = today.getDay(); // 0 = Sunday, 1 = Monday, etc.

  // Convert to Monday-based index
  const mondayBasedToday = currentDay === 0 ? 6 : currentDay - 1;

  // Calculate days to add/subtract to get to target day
  const daysFromToday = dayIndex - mondayBasedToday;

  const targetDate = new Date(today);
  targetDate.setDate(today.getDate() + daysFromToday);

  return targetDate;
};

/**
 * Formats a date string for display with proper timezone
 */
export const formatDateForTimezone = (
  date: Date | string,
  userTimezone: string,
  options?: Intl.DateTimeFormatOptions
): string => {
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;

    const defaultOptions: Intl.DateTimeFormatOptions = {
      timeZone: userTimezone,
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    };

    return dateObj.toLocaleString('en-US', { ...defaultOptions, ...options });
  } catch {
    return 'Invalid Date';
  }
};